import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ReportGroup, Report } from '../../../_models/reports';
import { ReportFilters } from '../../../_models/preferences';
import * as _ from 'lodash';
import { PortalApiService } from '../../../_services/portal-api.service';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ReportFavoriteService } from '../../_services/report-favorite-service';

@Component({
  selector: 'app-report-list-view',
  templateUrl: './report-list-view.component.html',
  styleUrl: './report-list-view.component.css'
})
export class ReportListViewComponent implements OnInit, OnDestroy {
  @Input() groupView: string;
  @Input() reportServer: string;
  @Input() filters: ReportFilters;
  @Input() groupID: number;
  availableReports: ReportGroup[] = [];
  favoriteGroups: ReportGroup[] = [];
  favoriteReports: ReportGroup = new ReportGroup();
  totalReports: ReportGroup = new ReportGroup();  
  noFavoriteReports: boolean;
  sortColumn?: string = null;
  sortDesc?: boolean = false;
  noFavReportsCardDisplay: boolean = false;
  favoriteErrorMessage: string = "";
  listDisplay: boolean = false;
  paginatePerPage: number = 24;
  p1: number = 1;
  availableReportsSort = {
    reportName: false,
    reportGroup: false
  };
  // Subjects
  private onDestroy$ = new Subject<void>();

  constructor(
    private apiService: PortalApiService,
    private reportFavService: ReportFavoriteService,
    private route: ActivatedRoute
  ) {
    this.reportFavService.availableReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.availableReports = updatedReports });
    this.reportFavService.favoriteGroups$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedGroups => { this.favoriteGroups = updatedGroups });
    this.reportFavService.favoriteReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.favoriteReports = updatedReports });
    this.reportFavService.totalReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedReports => { this.totalReports = updatedReports });
    this.reportFavService.noFavoriteReports$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedStatus => { this.noFavoriteReports = updatedStatus });
    this.reportFavService.favoriteErrorMessage$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedMessage => { this.favoriteErrorMessage = updatedMessage });
    this.reportFavService.queryReport$.pipe(takeUntil(this.onDestroy$)).subscribe(updatedQuery => { this.p1 = 1; });
  }

  ngOnInit() {
    // Check if the view is card or list display
    const queryParams = this.route.snapshot.queryParams;
    Object.keys(queryParams).forEach(param => {
      switch (param) {
        case 'listDisplay':
          const displayListStyle = queryParams[param];
          this.listDisplay = displayListStyle == "false" ? false : true;
          break;
      }
    });

    // Check if there are favorite reports in a group
    const isFavGroup = this.favoriteGroups.find(group => group.reportGroupID === this.groupID);
    this.noFavReportsCardDisplay = isFavGroup ? false : true;
  }

  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  sortReportsBy(column: string | any) {
    this.availableReportsSort[column] = !this.availableReportsSort[column];
    this.sortColumn = column;
    this.sortDesc = this.availableReportsSort[column];
  }

  addToFavorites(reportID: number, groupID: number) {
    this.apiService.addReportToFavorites(reportID)
      .subscribe({
        next: data => {
          this.newUpdateFavoritStatusForReports(reportID, groupID, true);
        },
        error: err => {
          this.favoriteErrorMessage = "Unable to add report #" + reportID + " to Favorites at this time";
          this.reportFavService.setFavoriteErrorMessage(this.favoriteErrorMessage);
          setInterval(() => {
            this.favoriteErrorMessage = null;
            this.reportFavService.setFavoriteErrorMessage(this.favoriteErrorMessage);
          }, 6000);
        }
      })
  }

  confirmRemove(reportID: number, groupID: number) {
    this.apiService.removeReportFromFavorites(reportID)
      .subscribe({
        next: data => {
          this.newUpdateFavoritStatusForReports(reportID, groupID, false);
        },
        error: err => {
          this.favoriteErrorMessage = "Unable to remove report #" + reportID + " from Favorites at this time";
          this.reportFavService.setFavoriteErrorMessage(this.favoriteErrorMessage);
          setInterval(() => {
            this.favoriteErrorMessage = null;
            this.reportFavService.setFavoriteErrorMessage(this.favoriteErrorMessage);
          }, 4000);
        }
      })
  }

  newUpdateFavoritStatusForReports(reportID: number, groupID: number, setAsFavorite: boolean) {
    let reportToUpdate: Report;

    /* Update Total Reports list */
    for (var i = 0; i < this.totalReports.reports.length; i++) {
      if (this.totalReports.reports[i].reportGroupID === groupID && this.totalReports.reports[i].reportID === reportID) {
        this.totalReports.reports[i].isFavorite = setAsFavorite;
        reportToUpdate = { ...this.totalReports.reports[i] };
        break;
      }
    }

    /* Update Available Reports list */
    for (var i = 0; i < this.availableReports.length; i++) {
      if (this.availableReports[i].reportGroupID === groupID) {
        for (var k = 0; k < this.availableReports[i].reports.length; k++) {
          if (this.availableReports[i].reports[k].reportID === reportID) {
            this.availableReports[i].reports[k].isFavorite = setAsFavorite;
            break;
          }
        }
        break;
      }
    }

    if (setAsFavorite) {
      /* Add to favorites and favorite groups */

      /* Update Favorite Reports list */
      this.favoriteReports.reports.push(reportToUpdate);
      if (this.noFavoriteReports) {
        this.noFavoriteReports = false;
      } else {
        this.favoriteReports.reports.sort(this.sortReportsByName);
      }

      /* Update Favorite Groups list */
      let favGroupIndex: number = -1;
      favGroupIndex = this.favoriteGroups.findIndex(group => group.reportGroupID === groupID);
      if (favGroupIndex > -1) {
        // Group already exists, add the favorite report to that group
        this.favoriteGroups[favGroupIndex].reports.push(reportToUpdate);
        this.favoriteGroups[favGroupIndex].reports.sort(this.sortReportsByName);
      }
      else {
        // Add the report in favorite group as the first favorite report
        this.favoriteGroups.push({ reportGroup: reportToUpdate.reportGroup, reportGroupID: reportToUpdate.reportGroupID, reports: [reportToUpdate] });
        this.favoriteGroups.sort(this.sortGroupsByName);
      }
    }
    else {
      /* Remove from favorite and favorite groups */

      /* Update Favorite Reports list */
      for (var i = 0; i < this.favoriteReports.reports.length; i++) {
        if (this.favoriteReports.reports[i].reportID == reportID) {
          this.favoriteReports.reports.splice(i, 1);
          break;
        }
      }
      if (this.favoriteReports.reports.length == 0) {
        this.noFavoriteReports = true;
      }

      /* Update Favorite Groups list */
      let favGroupIndex: number = -1;
      favGroupIndex = this.favoriteGroups.findIndex(group => group.reportGroupID === groupID);

      if (favGroupIndex > -1) {
        // Remove report from the favorite group
        if (this.favoriteGroups[favGroupIndex] && this.favoriteGroups[favGroupIndex].reports && this.favoriteGroups[favGroupIndex].reports.length > 0) {
          let favGroupReportIndex: number = this.favoriteGroups[favGroupIndex].reports.findIndex(report => report.reportID === reportID);
          if (favGroupReportIndex > -1) {
            this.favoriteGroups[favGroupIndex].reports.splice(favGroupReportIndex, 1);
          }
          // When a favorite group has no reports, remove the group
          if (this.favoriteGroups[favGroupIndex].reports.length == 0) {
            this.favoriteGroups.splice(favGroupIndex, 1);
          }
        }
      }
      /* Check for empty groups in Favorite Groups */
      for (var i = this.favoriteGroups.length - 1; i >= 0; i--) {
        if (!this.favoriteGroups[i].reports || this.favoriteGroups[i].reports.length == 0) {
          this.favoriteGroups.splice(i, 1);
        }
      }            
    }

    /* UPDATE ALL REPORT FAVORITES SERVICE SUBSCRIPTIONS */
    this.reportFavService.updateAvailableReports(this.availableReports);
    this.reportFavService.updateFavoriteReports(this.favoriteReports);
    this.reportFavService.setNoFavoriteReportsStatus(this.noFavoriteReports);
    this.reportFavService.updateFavoriteGroups(this.favoriteGroups);
    this.reportFavService.updateTotalReports(this.totalReports);
  }

  private sortGroupsByName(a: any, b: any) {
    var nameA = a.reportGroup.toUpperCase();
    var nameB = b.reportGroup.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  }

  private sortReportsByName(a: any, b: any) {
    var nameA = a.reportName.toUpperCase();
    var nameB = b.reportName.toUpperCase();
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  }
}
